import React, { useEffect, useRef, useCallback } from 'react';

/**
 * MediaPipe FaceMesh AR overlay — wraps a canvas over an existing <video>,
 * runs FaceMesh detection, and draws face-aware masks (dog ears, crown,
 * sunglasses, etc.) using emoji so we don't have to ship PNG assets.
 *
 * Props:
 *   videoRef : ref to the <video> playing the camera feed
 *   effectId : 'dog' | 'bunny' | 'cat' | 'crown_ar' | 'sunglasses' | 'mustache' | null
 *   mirrored : true when we're showing the front camera flipped (default true)
 *
 * FaceMesh CDN is loaded in index.html (defer). We poll until window.FaceMesh
 * is defined, then boot the detector.
 *
 * Masks use emoji glyphs sized and positioned via landmark geometry:
 *   - 10  = top-forehead centre (for hats/crowns/ears)
 *   - 234 = right temple (face-width anchor)
 *   - 454 = left temple
 *   - 33  = right-eye outer
 *   - 263 = left-eye outer
 *   - 1   = nose tip
 *   - 13  = upper lip centre (for mustache)
 */

const EFFECTS = {
  dog:        { top: '🐶', ears: true,  eyes: false, nose: '🐽', lip: null },
  bunny:      { top: '🐰', ears: true,  eyes: false, nose: null, lip: null },
  cat:        { top: '🐱', ears: true,  eyes: false, nose: null, lip: null },
  crown_ar:   { top: '👑', ears: false, eyes: false, nose: null, lip: null },
  sunglasses: { top: null, ears: false, eyes: '🕶️', nose: null, lip: null },
  mustache:   { top: null, ears: false, eyes: false, nose: null, lip: '👨' },
};

const FaceMeshOverlay = ({ videoRef, effectId, mirrored = true }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const faceMeshRef = useRef(null);
  const latestLandmarksRef = useRef(null);

  // Init FaceMesh once the CDN script is ready
  useEffect(() => {
    let disposed = false;
    const boot = () => {
      if (disposed) return;
      if (!window.FaceMesh) { setTimeout(boot, 200); return; }
      try {
        const fm = new window.FaceMesh({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4/${file}`,
        });
        fm.setOptions({
          maxNumFaces: 2,
          refineLandmarks: false,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });
        fm.onResults((results) => {
          latestLandmarksRef.current = results.multiFaceLandmarks || null;
        });
        faceMeshRef.current = fm;
      } catch (e) {
        // Silent — overlay just won't render
      }
    };
    boot();
    return () => { disposed = true; };
  }, []);

  // Detection loop
  const tick = useCallback(async () => {
    const video = videoRef?.current;
    const canvas = canvasRef.current;
    const fm = faceMeshRef.current;
    if (video && canvas && fm && video.readyState >= 2 && !video.paused) {
      try { await fm.send({ image: video }); } catch {}
      // Match canvas to displayed video size
      const w = video.clientWidth;
      const h = video.clientHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
      }
      draw(canvas, latestLandmarksRef.current, effectId, mirrored, video);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [videoRef, effectId, mirrored]);

  useEffect(() => {
    if (!effectId || effectId === 'none') {
      // Clear + stop
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const c = canvasRef.current;
      if (c) { const ctx = c.getContext('2d'); ctx?.clearRect(0, 0, c.width, c.height); }
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [effectId, tick]);

  if (!effectId || effectId === 'none' || !EFFECTS[effectId]) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="facemesh-overlay"
    />
  );
};

// --------- Drawing helpers ---------

function draw(canvas, facesLandmarks, effectId, mirrored, video) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!facesLandmarks || !facesLandmarks.length) return;
  const eff = EFFECTS[effectId];
  if (!eff) return;

  const W = canvas.width;
  const H = canvas.height;

  for (const lms of facesLandmarks) {
    // In the normalised FaceMesh coords (0–1), x=0 is LEFT of camera feed.
    // When mirrored (front camera flipped), we flip x → (1 - x) so the overlay
    // visually lines up with what the user sees.
    const L = (idx) => {
      const p = lms[idx];
      return { x: (mirrored ? 1 - p.x : p.x) * W, y: p.y * H };
    };

    const foreheadTop   = L(10);
    const chin          = L(152);
    const rightTemple   = L(234);
    const leftTemple    = L(454);
    const noseTip       = L(1);
    const lipCentre     = L(13);
    const eyeRightOuter = L(33);
    const eyeLeftOuter  = L(263);
    const eyeRightIn    = L(133);
    const eyeLeftIn     = L(362);

    const faceWidth = Math.hypot(leftTemple.x - rightTemple.x, leftTemple.y - rightTemple.y);
    const faceHeight = Math.hypot(chin.x - foreheadTop.x, chin.y - foreheadTop.y);
    const rollAngle = Math.atan2(leftTemple.y - rightTemple.y, leftTemple.x - rightTemple.x);

    // TOP mask (hats, ears anchor) — positioned above forehead, centred
    if (eff.top) {
      const size = faceWidth * 1.3;
      const cx = foreheadTop.x;
      const cy = foreheadTop.y - size * 0.35; // sit above the head
      drawEmojiRotated(ctx, eff.top, cx, cy, size, rollAngle);
    }

    // Side ears (separate from the top glyph) — for dog/bunny/cat
    if (eff.ears) {
      const earSize = faceWidth * 0.55;
      // right ear
      drawEmojiRotated(ctx, eff.top, rightTemple.x - earSize * 0.1, rightTemple.y - earSize * 0.55, earSize, rollAngle);
      // left ear
      drawEmojiRotated(ctx, eff.top, leftTemple.x + earSize * 0.1, leftTemple.y - earSize * 0.55, earSize, rollAngle);
    }

    // EYES mask — sunglasses/visor spans eye-to-eye
    if (eff.eyes) {
      const eyeCx = (eyeRightOuter.x + eyeLeftOuter.x) / 2;
      const eyeCy = (eyeRightOuter.y + eyeLeftOuter.y) / 2;
      const size = Math.hypot(eyeLeftOuter.x - eyeRightOuter.x, eyeLeftOuter.y - eyeRightOuter.y) * 1.6;
      drawEmojiRotated(ctx, eff.eyes, eyeCx, eyeCy, size, rollAngle);
    }

    // NOSE mask — dog nose etc.
    if (eff.nose) {
      const size = faceWidth * 0.28;
      drawEmojiRotated(ctx, eff.nose, noseTip.x, noseTip.y, size, rollAngle);
    }

    // LIP mask — mustache above upper lip
    if (eff.lip) {
      const size = faceWidth * 0.55;
      drawEmojiRotated(ctx, eff.lip, lipCentre.x, lipCentre.y - size * 0.05, size, rollAngle);
    }
  }
}

function drawEmojiRotated(ctx, emoji, cx, cy, size, angle) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  ctx.font = `${Math.max(16, Math.round(size))}px system-ui, "Apple Color Emoji", "Segoe UI Emoji"`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 0, 0);
  ctx.restore();
}

export default FaceMeshOverlay;
